import { useState } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import cx from 'classnames'

import Button from 'common/components/button'
import ConfirmationModal from 'common/components/confirmationModal/ConfirmationModal'
import { Bin, Edit, MoreOptionsVertical } from 'common/icons'

import useDeleteMergeTagMutation from '../../../api/mutations/useDeleteMergeTagMutation'

type Props = {
  tagId: string
  tagName: string
  onEdit: () => void
}

const MergeTagsActions = ({ tagId, tagName, onEdit }: Props) => {
  const [showDeleteTagModal, setShowDeleteTagModal] = useState<boolean>(false)
  const { mutate, isPending: isLoading } = useDeleteMergeTagMutation({
    tagId,
    tagName,
    onSuccess: () => {
      setShowDeleteTagModal(false)
    },
  })

  return (
    <>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <Button
            variant="icon"
            data-testid="Tags actions"
            suffixIcon={
              <MoreOptionsVertical
                isDefaultColor={false}
                className="text-base-700"
              />
            }
          />
        </DropdownMenu.Trigger>
        <DropdownMenu.Portal>
          <DropdownMenu.Content className="p-2 z-40 bg-white border border-coolGray-200 rounded shadow-lg min-w-[120px]">
            <DropdownMenu.Item
              onSelect={onEdit}
              disabled={isLoading}
              aria-label="Edit tag"
              className={cx(
                'flex items-center hover:bg-gold-300 text-xs text-base-700 px-2 py-1 outline-none rounded',
                {
                  'cursor-not-allowed opacity-50': isLoading,
                  'cursor-pointer': !isLoading,
                }
              )}
            >
              <Edit
                isDefaultColor={false}
                size={4}
                className="mr-4 text-base-700"
              />
              Edit
            </DropdownMenu.Item>
            <DropdownMenu.Item
              onSelect={() => setShowDeleteTagModal(true)}
              disabled={isLoading}
              aria-label="Delete tag"
              className={cx(
                'flex items-center hover:bg-gold-300 text-xs text-base-700 px-2 py-1 outline-none rounded',
                {
                  'cursor-not-allowed opacity-50': isLoading,
                  'cursor-pointer': !isLoading,
                }
              )}
            >
              <Bin
                isDefaultColor={false}
                size={4}
                className="mr-4 text-base-700"
              />
              Delete
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
      <ConfirmationModal
        data-testid="remove-tag-modal"
        title="Delete personalisation tag"
        confirmationText={`Are you sure you want to delete the "${tagName}" personalisation tag? Campaigns already using this tag will continue to use it until they are updated manually.`}
        confirmButtonText="YES, DELETE"
        cancelButtonText="CANCEL"
        open={showDeleteTagModal}
        hasConfirmationSlider={true}
        onConfirm={() => mutate()}
        onCancel={() => setShowDeleteTagModal(false)}
        isLoading={isLoading}
      />
    </>
  )
}

export default MergeTagsActions
